/* eslint-disable max-lines */
// deps
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";

// external-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { PAYMENT_CODES, SPLITPAY_METHOD_STORAGE_KEY, DEFAULT_SPLITPAY_METHOD } from "@swiggy-private/payment-types";
import {
    CheckoutActionTypes,
    CheckoutContext,
    useNavigate,
    closeWebview,
    DineoutCartContext,
    DineoutCartType,
    IDineoutLocalCart as ILocalCart,
    BLAZE_EVENTS,
    useSessionStorage,
    NEWRELIC_TABLES,
    recordNREvent,
    GlobalHeaderState,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";
import {
    GenericError,
    RouteProps,
    CartModal,
    CouponMessageCard,
    UpdateProgressLoader,
    useEffectOnce,
} from "@swiggy-private/core-ui";
import { DineoutCheckoutRoute } from "@swiggy-private/checkout-types";

// components
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";
import { ErrorWidget } from "@swiggy-private/dineout-cart-ui";
import { DineoutPaymentCart } from "@dineout-cart-client/components/payment-cart";

// hooks
import { useBillPaymentAnalytics } from "@dineout-cart-client/hooks/useDineoutCartAnalytics";
import useFetchDineoutCart from "@dineout-cart-client/hooks/useFetchDineoutCart";
import { usePerformUpdateCart } from "@dineout-cart-client/hooks/usePerformUpdateCart";
import useDineoutCartActions from "@dineout-cart-client/hooks/useDineoutCartActions";
import { useDineCashPolling } from "@dineout-cart-client/hooks/useDineCashPolling";
import { usePrefetchDineCashPollingLottie } from "@dineout-cart-client/hooks/usePrefetchLottie";

// contexts
import { UpdateCartContext } from "@dineout-cart-client/contexts/UpdateCartContext";

// constants
import {
    CUSTOM_ERROR_PAYLOAD,
    DEFAULT_BILL_PAYMENT_COMPONENT_PAYLOAD,
    ERROR_STATUS_CODES,
    LOADING_TEXT,
    RECORD_PERCIEVED_TIME_EVENTS,
    RETRY,
    QR_INVALID_IMAGE_ID,
    INACTIVE,
    PoolingMetricsSource,
} from "@dineout-cart-client/constants/index";

// helpers
import { navigateToLink } from "@swiggy-private/dineout-payment-success-ui";
import { isPaymentFlowScanQR, getErrorPayloadData, getIsQRInvalid } from "@dineout-cart-client/helpers/cartHelper";
import { persistDineCashPoolingIntent } from "@dineout-cart-client/helpers/DineCashPooling";
import { recordCartPercievedTime } from "@dineout-cart-client/utils/Metric";

// interfaces
import { IErrorMeta } from "@dineout-cart-client/interfaces";
import { CartMetaData } from "@dineout-cart-client/interfaces/dineoutCartTypes";

// styles
import { LoadingContainer, ToastContainer } from "@dineout-cart-client/components/table-reservation/styles";
import { PaymentAlert } from "@swiggy-private/core-ui";

const BillPaymentCartRenderer = (_props: RouteProps<DineoutCheckoutRoute>) => {
    const { cartState } = useContext(DineoutCartContext);
    const { localCart, reviewedCart: wholeApiResponse } = cartState;
    const reviewedCart = wholeApiResponse?.data;
    const { loading, error: cartApiError, uniqueIdForCart } = useFetchDineoutCart(DineoutCartType.BILL_PAYMENT_CART);
    const [softError, setSoftError] = useState<string>();
    const [errorPayload, setErrorPayload] = useState<IErrorMeta>();
    const [distanceBasedAlert, setDistanceBasedAlert] = useState("");
    const [enteredAmount, setEnteredAmount] = useState(0);
    const [billPayload, setBillPayload] = useState(DEFAULT_BILL_PAYMENT_COMPONENT_PAYLOAD);
    const [initialCartLoaded, setInitialCartLoaded] = useState(false);
    const [splitPayMethod, setSplitPayMethod] = useSessionStorage(SPLITPAY_METHOD_STORAGE_KEY, DEFAULT_SPLITPAY_METHOD);
    usePrefetchDineCashPollingLottie(reviewedCart);
    const { showPopup, onCloseModal, couponModalData } = useContext(UpdateCartContext);
    // TODOs: need to replace this with setSoft error or added in context
    const { onClickProceed } = useDineoutCartActions({
        skipPayment: false,
        setSoftError,
        setErrorPayload,
    });
    const { onClickProceed: onClickSettleBill } = useDineoutCartActions({
        skipPayment: true,
        setSoftError,
    });

    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);

    const navigateTo = useNavigate();
    const {
        onClickProceedToPay,
        onScanQrErrorPageImpression,
        onScanQrErrorPageBackClick,
        onScanQrErrorPagePrimaryActionClick,
        onScanQRBottomPopupScanAgainImpression,
        onScanQRBottomPopupScanAgainClick,
    } = useBillPaymentAnalytics();
    // TODOs: need to replace this with setSoft error or added in context
    const { updateCart, loading: updateCartLoading } = usePerformUpdateCart({
        setErrorPayload,
    });

    const onDineCashPoolUpdated = useCallback(() => {
        if (!isEmpty(localCart)) {
            updateCart(localCart);
        }
    }, [localCart, updateCart]);

    const { startPolling, stopPolling, shouldPoll } = useDineCashPolling({
        onDineCashPoolUpdated,
        source: PoolingMetricsSource.CART,
    });

    const goBack = useCallback(() => {
        closeWebview();
    }, []);

    useEffectOnce(() => {
        try {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                eventSubType: "dineout_payment_cart_onload",
                type: "DINEOUT_PAYMENT_CART_ONLOAD",
                checkoutStateType: checkoutState.type,
                checkoutStatePayload: JSON.stringify(checkoutState.payload),
            });
        } catch (error) {
            /* No-op*/
        }
    });

    /** This is for MMT option to not be selected if user goes back to cart screen */
    useEffect(() => {
        const mmtCode = PAYMENT_CODES?.MMT?.toLowerCase?.() ?? "";
        if (mmtCode && splitPayMethod.toLowerCase().includes(mmtCode)) {
            setSplitPayMethod(DEFAULT_SPLITPAY_METHOD);
        }
    }, [setSplitPayMethod, splitPayMethod]);

    const scanAgainEventPayload = useMemo(() => {
        const { paymentFlow, payBillSource, cartMetaData } = localCart || {};
        const { restaurantId } = cartMetaData || {};
        return {
            restaurantId: String(restaurantId || 0),
            paymentFlow: paymentFlow || "",
            paybillSource: payBillSource || "",
            qrStatus: INACTIVE,
        };
    }, [localCart]);

    const handleErrorWidgetImpression = useCallback(() => {
        onScanQRBottomPopupScanAgainImpression(scanAgainEventPayload);
    }, [onScanQRBottomPopupScanAgainImpression, scanAgainEventPayload]);

    const handleErrorWidgetClick = useCallback(() => {
        onScanQRBottomPopupScanAgainClick(scanAgainEventPayload);
    }, [onScanQRBottomPopupScanAgainClick, scanAgainEventPayload]);

    const errorPayloadData = getErrorPayloadData(errorPayload, handleErrorWidgetImpression, handleErrorWidgetClick);
    const isQRInvalid = getIsQRInvalid(errorPayload);
    useEffect(() => {
        if (!loading && cartState != null && !initialCartLoaded) {
            recordCartPercievedTime(
                BLAZE_EVENTS.RECORD_BILL_PAYMENT_CART_LOAD_TIME,
                RECORD_PERCIEVED_TIME_EVENTS.BILL_PAYMENT_APP_LOAD_TIME,
            );
            setInitialCartLoaded(true);
        }
    }, [cartState, initialCartLoaded, loading]);

    useEffect(() => {
        setEnteredAmount(localCart?.cartMetaData?.billAmount || 0);
        setDistanceBasedAlert(localCart?.distanceFromRx || "");
    }, [localCart]);

    useEffect(() => {
        if (typeof window !== "undefined") {
            window.history.pushState(null, "", window.location.href);
            const handleBack = () => {
                goBack();
            };

            window.addEventListener("popstate", handleBack);

            return () => {
                window.removeEventListener("popstate", handleBack);
            };
        }
    }, [goBack]);

    useEffect(() => {
        if (reviewedCart) {
            setBillPayload({
                billToPay: reviewedCart?.bill?.dineoutPricingInfo?.billToPay || 0,
                totalBillExcludingExtras: reviewedCart?.bill?.dineoutPricingInfo?.totalBillExcludingExtras || 0,
                billAmountToApplyCoupon: reviewedCart?.bill?.dineoutPricingInfo?.billAmountToApplyCoupon || 0,
            });
        }
    }, [reviewedCart]);

    const onClickApplyCoupon = useCallback(() => {
        setCheckoutState({
            type: CheckoutActionTypes.SHOW_COUPON_LISTING,
            payload: {
                couponListingLaunchTimestamp: Date.now(),
            },
        });
        navigateTo(DineoutCheckoutRoute.COUPON_LISTING);
    }, [setCheckoutState, navigateTo]);

    const onProceedToPay = useCallback(() => {
        stopPolling();
        const { paymentFlow, paybillSource, restaurantId } = scanAgainEventPayload || {};
        if (isPaymentFlowScanQR(paymentFlow)) {
            onClickProceedToPay({
                restaurantID: Number(restaurantId || 0),
                flow: paymentFlow,
                paybillSource,
            });
        } else {
            onClickProceedToPay({
                restaurantID: reviewedCart?.storesInfo?.[0]?.id || 0,
                flow: "",
                cashbackEarned: reviewedCart?.bill?.dineoutPricingInfo?.totalRewardsRedeemed || 0,
                totalBill: reviewedCart?.bill?.dineoutPricingInfo?.billToPay || 0,
                totalDiscount: reviewedCart?.bill?.dineoutPricingInfo?.totalDiscount || 0,
            });
        }
        onClickProceed();
    }, [
        onClickProceed,
        onClickProceedToPay,
        reviewedCart?.storesInfo,
        scanAgainEventPayload,
        reviewedCart?.bill?.dineoutPricingInfo?.totalRewardsRedeemed,
        reviewedCart?.bill?.dineoutPricingInfo?.billToPay,
        reviewedCart?.bill?.dineoutPricingInfo?.totalDiscount,
        stopPolling,
    ]);

    useEffect(() => {
        if (shouldPoll) {
            startPolling();
        }
    }, [startPolling, shouldPoll]);

    const onSettleBillPress = useCallback(() => {
        onClickSettleBill();
    }, [onClickSettleBill]);

    const onClickRemoveCoupon = useCallback(() => {
        try {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                type: "REMOVE_COUPON",
            });
        } catch {
            /* No-op */
        }
        const updatedLocalCart: ILocalCart = {
            ...localCart,
            cartMetaData: {
                ...localCart?.cartMetaData,
                couponCode: null,
            } as CartMetaData,
        };
        updateCart(updatedLocalCart);
    }, [localCart, updateCart]);

    const updateTipValue = useCallback(
        (val: number) => {
            try {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                    type: "TIP_SELECTION",
                });
            } catch {
                /* No-op */
            }

            const updatedLocalCart: ILocalCart = {
                ...localCart,
                cartMetaData: {
                    ...localCart?.cartMetaData,
                    restaurantTip: val,
                    couponCode: localCart?.cartMetaData?.couponCode || null,
                } as CartMetaData,
            };
            updateCart(updatedLocalCart);
        },
        [localCart, updateCart],
    );

    const onRedeemCash = useCallback(
        (redeemValue: boolean, redeemableRewards: number) => {
            try {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                    type: "REDEEM_CASH",
                });
            } catch {
                /* No-op */
            }
            const updatedLocalCart: ILocalCart = {
                ...localCart,
                cartMetaData: {
                    ...localCart?.cartMetaData,
                    rewardsWidget: {
                        rewardsRedeemed: redeemValue,
                        redeemableRewards: redeemableRewards,
                    },
                } as CartMetaData,
            };
            updateCart(updatedLocalCart);
        },
        [localCart, updateCart],
    );

    const onDineCashPoolingClick = useCallback(() => {
        startPolling();
        const poolCartId = reviewedCart?.cartId ?? "";
        const userId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId) || "";
        persistDineCashPoolingIntent(poolCartId, userId);
        navigateTo(DineoutCheckoutRoute.DINE_CASH_POOLING);
    }, [navigateTo, reviewedCart?.cartId, startPolling]);

    const reloadCart = useCallback(() => {
        window.location.reload();
    }, []);

    const cartApiErrorPayload = cartApiError?.errorPayload;
    const {
        statusCode: errorStatusCode,
        statusMessage: errorStatusMessage,
        statusTitle: errorStatusTitle,
        ctaAction: errorCtaAction,
        ctaText: errorCtaText,
    } = cartApiErrorPayload || {};

    const onClickErrorPrimaryAction = useCallback(() => {
        onScanQrErrorPagePrimaryActionClick(scanAgainEventPayload);
        if (errorCtaAction?.meta?.deeplink) {
            navigateToLink(errorCtaAction.meta.deeplink);
        }
    }, [errorCtaAction?.meta?.deeplink, onScanQrErrorPagePrimaryActionClick, scanAgainEventPayload]);

    const onErrorPageBackClick = useCallback(() => {
        onScanQrErrorPageBackClick(scanAgainEventPayload);
        goBack();
    }, [onScanQrErrorPageBackClick, scanAgainEventPayload, goBack]);

    const errorPageImpressionEvent = useCallback(() => {
        onScanQrErrorPageImpression(scanAgainEventPayload);
    }, [onScanQrErrorPageImpression, scanAgainEventPayload]);

    const resetSoftError = useCallback(() => {
        setSoftError(undefined);
    }, []);

    const renderLoadingState = useCallback(
        () => (
            <LoadingContainer>
                <Loader
                    size="large"
                    type="secondary"
                    foregroundColor="positive"
                    backgroundColor="positive_Sub"
                    isDisabled={false}
                />
                {LOADING_TEXT}
            </LoadingContainer>
        ),
        [],
    );

    const handlePaymentAlertAction = useCallback(() => {
        setCheckoutState({ type: CheckoutActionTypes.DINEOUT_BILL_PAYMENT_CART });
        if (isEmpty(localCart)) return;
        updateCart(localCart);
    }, [setCheckoutState, localCart, updateCart]);

    if (loading && !reviewedCart) return renderLoadingState(); //

    if (errorStatusCode == ERROR_STATUS_CODES.DEAL_ALREADY_BOOKED) {
        return (
            <GenericError
                action={{
                    onClick: reloadCart,
                    text: RETRY,
                }}
                title={CUSTOM_ERROR_PAYLOAD.TITLE}
                message={errorStatusMessage}
                imageId={CUSTOM_ERROR_PAYLOAD.IMAGE}
            />
        );
    } else if (errorStatusCode == ERROR_STATUS_CODES.QR_INVALID) {
        return (
            <GenericError
                primaryAction={{
                    onClick: onClickErrorPrimaryAction,
                    text: errorCtaText || "",
                }}
                action={null}
                title={errorStatusTitle || ""}
                message={errorStatusMessage || ""}
                imageId={QR_INVALID_IMAGE_ID}
                onBackCTA={onErrorPageBackClick}
                pageImpressionEvent={errorPageImpressionEvent}
                isHalfHeightImage={true}
            />
        );
    }

    if (!reviewedCart) {
        return <GenericError action={{ onClick: reloadCart, text: RETRY }} dataTestId={"dineout-cart-error"} />;
    }

    return (
        <>
            {updateCartLoading && <UpdateProgressLoader />}
            <DineoutPaymentCart
                distanceBasedAlert={distanceBasedAlert}
                enteredAmount={Number(enteredAmount)}
                totalBillExcludingExtras={billPayload.billAmountToApplyCoupon || billPayload.totalBillExcludingExtras}
                onClickApplyCoupon={onClickApplyCoupon}
                onClickRemoveCoupon={onClickRemoveCoupon}
                onProceedToPay={onProceedToPay}
                billToPay={billPayload.billToPay}
                doCart={reviewedCart}
                onUpdateTip={updateTipValue}
                onSettleBillPress={onSettleBillPress}
                onRedeemCash={onRedeemCash}
                scanAgainEventPayload={scanAgainEventPayload}
                uniqueIdForCart={uniqueIdForCart}
                dineCashPoolingWidgetData={reviewedCart?.rewardsPoolWidget}
                onDineCashPoolingClick={onDineCashPoolingClick}
                handlePaymentAlertAction={handlePaymentAlertAction}
                onTurboPayClick={stopPolling}
            />
            {isQRInvalid && (
                <HalfCard isOpen={isQRInvalid} hideCloseIcon closeOnOverlayClick={false}>
                    <ErrorWidget {...errorPayloadData} />
                </HalfCard>
            )}
            {!isEmpty(softError) && (
                <ToastContainer>
                    <Toast message={softError} containerClass={"toast"} show autoDismiss onDismiss={resetSoftError} />
                </ToastContainer>
            )}
            {showPopup ? <CartModal data={couponModalData as CouponMessageCard} closeModal={onCloseModal} /> : null}
            {checkoutState.type === CheckoutActionTypes.GENERIC_PAYMENT_ALERT && (
                <PaymentAlert {...checkoutState.payload} />
            )}
        </>
    );
};

export default memo(BillPaymentCartRenderer);
